import React, { useEffect, useMemo, useState } from 'react';
import { PRESENTER_COMMENT_MESSAGE_COLLAPSED_MAX_CHARS } from '../../../../constants/presenterComments';

/** text-sm + leading-5 → 3 lines */
const COLLAPSED_MESSAGE_MAX_HEIGHT = '3.75rem';

const readToggleClassName =
  'inline text-xs font-medium text-indigo-600 hover:text-indigo-800 focus:outline-none focus:underline';

function formatCommentDate(isoDate) {
  if (!isoDate) return '—';
  try {
    return new Date(isoDate).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoDate;
  }
}

function CommentCard({ comment }) {
  const [expanded, setExpanded] = useState(false);

  const message = comment?.message?.trim() ? comment.message : '—';
  const isLong = message !== '—' && message.length > PRESENTER_COMMENT_MESSAGE_COLLAPSED_MAX_CHARS;

  useEffect(() => {
    setExpanded(false);
  }, [comment?.id]);

  const formattedDate = useMemo(
    () => formatCommentDate(comment?.date),
    [comment?.date]
  );

  return (
    <article className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-left">
      <div className="text-xs font-semibold text-gray-900">{comment.username || '—'}</div>

      {isLong && !expanded ? (
        <div className="relative mt-1">
          <p
            className="text-sm leading-5 text-gray-700 whitespace-normal break-words [text-wrap:auto] overflow-hidden"
            style={{ maxHeight: COLLAPSED_MESSAGE_MAX_HEIGHT }}
          >
            {message}
          </p>
          <div
            className="pointer-events-none absolute bottom-0 right-0 flex items-end pl-10"
            style={{
              background:
                'linear-gradient(to left, rgb(249 250 251) 55%, rgb(249 250 251 / 0.95) 75%, transparent)',
            }}
          >
            <span className="pointer-events-auto inline-flex items-baseline gap-0.5 text-sm leading-5">
              <span className="text-gray-700" aria-hidden>
                …{' '}
              </span>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                aria-expanded={false}
                className={readToggleClassName}
              >
                Read more
              </button>
            </span>
          </div>
        </div>
      ) : (
        <p className="mt-1 text-sm leading-5 text-gray-700 whitespace-normal break-words [text-wrap:auto]">
          {message}
          {isLong && expanded && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-expanded
                className={readToggleClassName}
              >
                Read less
              </button>
            </>
          )}
        </p>
      )}

      <time
        className="mt-1.5 block text-xs text-gray-500"
        dateTime={comment.date}
      >
        {formattedDate}
      </time>
    </article>
  );
}

export default React.memo(CommentCard);
